/**
 * Trip zones for external service: the catalog's `zone_N_trip_charge` rows, each covering driving miles up to its
 * `radius_miles`. The Service Tickets estimate and new external tickets both price the trip with these.
 */

export type TripChargeCatalogRow = {
  code: string;
  label: string;
  category: string;
  unit: string;
  amount: number;
  radius_miles: number | null;
  active: boolean;
};

export type TripZone<Row extends TripChargeCatalogRow = TripChargeCatalogRow> = {
  /** The N in `zone_N_trip_charge`. */
  number: number;
  /** Driving miles the zone starts after (0 for the first zone). */
  fromMiles: number;
  /** Driving miles the zone covers, inclusive. */
  toMiles: number;
  charge: Row;
};

export type TripZoneMatch<Row extends TripChargeCatalogRow = TripChargeCatalogRow> = {
  zone: TripZone<Row>;
  /** Past the last zone: the last zone's charge plus extra travel, quoted per job. */
  beyond: boolean;
};

const TRIP_ZONE_CODE = /^zone_(\d+)_trip_charge$/;

/** Active zone trip charges, nearest first. */
export function tripZonesFromCatalog<Row extends TripChargeCatalogRow>(charges: readonly Row[]): TripZone<Row>[] {
  const rows = charges
    .filter((charge) => (
      charge.active
      && charge.category === 'travel'
      && charge.unit === 'flat'
      && TRIP_ZONE_CODE.test(charge.code)
      && Number.isFinite(Number(charge.radius_miles))
      && Number(charge.radius_miles) > 0
    ))
    .sort((a, b) => Number(a.radius_miles) - Number(b.radius_miles));
  return rows.map((charge, index) => ({
    number: Number(TRIP_ZONE_CODE.exec(charge.code)?.[1] ?? index + 1),
    fromMiles: index === 0 ? 0 : Number(rows[index - 1].radius_miles),
    toMiles: Number(charge.radius_miles),
    charge,
  }));
}

/** The zone that covers a drive. Past the last zone it returns the last zone, marked `beyond`. */
export function tripZoneForMiles<Row extends TripChargeCatalogRow>(
  zones: readonly TripZone<Row>[],
  drivingMiles: number | null | undefined,
): TripZoneMatch<Row> | null {
  const miles = Number(drivingMiles);
  if (!zones.length || drivingMiles == null || !Number.isFinite(miles) || miles < 0) return null;
  const zone = zones.find((candidate) => miles <= candidate.toMiles);
  return zone ? { zone, beyond: false } : { zone: zones[zones.length - 1], beyond: true };
}
